import { formIsInProgress } from "./form.js";
/**
 * The team whose admission work item carries a form's redo: that of its PART-1
 * SIGNATORY.
 *
 * Deliberately the ORIGINAL version rather than `ownerTeamId`: an amended form
 * still belongs, for redo purposes, to the team that first signed it, and
 * ownership can move without the work item moving.
 */
export function redoAuthorTeamId(form) {
    return form.versions.at(0)?.signatures.at(0)?.teamId ?? undefined;
}
/**
 * The redo already in flight on `teamId`'s work item for this admission, if any,
 * ignoring anything belonging to `excludeFormId` itself.
 *
 * Redo state for "who must act" lives on the admission's work item, and there is
 * exactly ONE work item per (form context, team) — a unique index. A team
 * therefore has room for exactly one redo per admission: starting a second one
 * overwrites the first's target form, reasons and requester, stranding the first
 * form in a redo holding state with no work item behind it, where it can no
 * longer be accepted, declined, retracted or redone again.
 *
 * A team's row is committed to a redo in exactly two phases:
 *
 * - REQUESTED — the row carries requestType "redo" naming another form.
 * - IN PROGRESS — another form's replacement is still being completed. Accepting
 *   a redo and self-initiating one both repoint this same row at the
 *   replacement, so the replacement identifies the committed team: either it is
 *   owned by them, or their row already points at it (the part-2 team of a joint
 *   replacement).
 *
 * A form that merely INHERITED a redo holding state from an upstream redo has
 * neither signal — no work item and no replacement of its own — so it never
 * blocks. That is what keeps the escape hatch open: a form soft-locked by an
 * upstream redo may still start a redo of its own.
 *
 * Only forms that actually live in `formContextId` count. A context payload also
 * carries upstream forms pulled in from OTHER contexts by a (possibly severed)
 * link, and their redos sit on a different row — (their context, this team) — so
 * counting them would refuse a redo that cannot collide with anything.
 */
export function findBlockingTeamRedo(args) {
    const { teamId, formContextId, excludeFormId, contextForms, contextWorkItems, } = args;
    const siblings = contextForms.filter((f) => f.formContextId === formContextId && f.id !== excludeFormId);
    const siblingIds = new Set(siblings.map((f) => f.id));
    // Matching on the sibling set rather than on `formId !== excludeFormId` also
    // drops work items naming a form in another context.
    const request = contextWorkItems.find((w) => w.requestType === "redo" &&
        w.teamId === teamId &&
        !!w.formId &&
        siblingIds.has(w.formId));
    if (request?.formId) {
        return { blockingFormId: request.formId, phase: "requested" };
    }
    const replacement = siblings.find((f) => !!f.redoneFromFormId &&
        f.redoneFromFormId !== excludeFormId &&
        formIsInProgress(f) &&
        (f.ownerTeamId === teamId ||
            contextWorkItems.some((w) => w.teamId === teamId && w.formId === f.id)));
    if (replacement?.redoneFromFormId) {
        return {
            blockingFormId: replacement.redoneFromFormId,
            phase: "in-progress",
        };
    }
    return undefined;
}
/**
 * Every form that `form` replaced through a chain of redos, following
 * `redoneFromFormId` back through `forms`. A replaced form missing from `forms`
 * is included, but the walk cannot see past it.
 */
export function redoneFromFormIds(args) {
    const formsById = new Map(args.forms.map((f) => [f.id, f]));
    const replacedIds = new Set();
    let replacedId = args.form.redoneFromFormId;
    while (replacedId != null && !replacedIds.has(replacedId)) {
        replacedIds.add(replacedId);
        replacedId = formsById.get(replacedId)?.redoneFromFormId;
    }
    return replacedIds;
}
//# sourceMappingURL=redo.js.map